import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatPoints } from "@/lib/format";
import { formatPesos } from "@/lib/currency";
import { Input } from "@/components/ui/input";
import {
    Crown, CheckCircle2, XCircle, Award, Target, Trophy, TrendingUp,
    BarChart3, Calendar, MailCheck, MailX, Users, Wallet, Star,
    Sparkles, Search, UserPlus, ShieldCheck,
} from "lucide-react";

const STATUS_TABS = [
    { id: "pending", label: "Pendientes", color: "amber" },
    { id: "approved", label: "Aprobadas", color: "green" },
    { id: "rejected", label: "Rechazadas", color: "rose" },
];

export default function AdminCreatorsTab() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState(null);

    const load = async (status) => {
        try {
            const { data } = await api.get(`/admin/creator-applications?status=${status}`);
            setItems(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar");
        }
    };
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/creator-applications?status=${tab}`);
                if (!cancel) setItems(data || []);
            } catch (e) {
                if (!cancel) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar");
            }
        })();
        return () => { cancel = true; };
    }, [tab]);

    const reload = () => load(tab);

    return (
        <div className="space-y-5" data-testid="admin-creators-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    {STATUS_TABS.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => setTab(s.id)}
                            data-testid={`creators-filter-${s.id}`}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition border ${
                                tab === s.id
                                    ? `bg-p2w-purple text-white border-p2w-purple`
                                    : "bg-p2w-elevated text-p2w-gray border-p2w-line hover:text-white"
                            }`}
                        >
                            {s.label}
                            {items && tab === s.id && <span className="ml-2 text-[10px] opacity-80">({items.length})</span>}
                        </button>
                    ))}
                </div>
                <ManualGrantDialog onDone={reload} />
            </div>

            {items === null ? (
                <div className="h-72 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" />
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-12 text-center">
                    <Crown className="w-10 h-10 mx-auto text-p2w-gray/50" />
                    <p className="mt-3 text-p2w-gray">No hay solicitudes {tab === "pending" ? "pendientes" : tab === "approved" ? "aprobadas" : "rechazadas"}.</p>
                </div>
            ) : (
                <div className="grid lg:grid-cols-2 gap-4">
                    {items.map((app) => <CreatorAppCard key={app.id} app={app} onChange={reload} />)}
                </div>
            )}
        </div>
    );
}

function CreatorAppCard({ app, onChange }) {
    const u = app.user;
    const m = app.metrics;
    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden" data-testid={`creator-app-${app.id}`}>
            {/* Header */}
            <div className="p-5 border-b border-p2w-line flex items-start gap-4">
                <Avatar className="w-14 h-14 ring-1 ring-p2w-purple/30">
                    <AvatarImage src={u.avatar_url} />
                    <AvatarFallback className="bg-p2w-purple text-white font-sora font-bold">
                        {(u.username || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-sora font-bold text-white text-lg">@{u.username}</h3>
                        {u.email_verified
                            ? <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-p2w-green font-semibold"><MailCheck className="w-3 h-3" /> verificado</span>
                            : <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-amber-400 font-semibold"><MailX className="w-3 h-3" /> sin verif.</span>}
                    </div>
                    <p className="text-xs text-p2w-gray break-all">{u.email}</p>
                    <p className="text-[11px] text-p2w-gray mt-1 number-tabular">
                        Solicitado: <span className="text-white">{formatDt(app.created_at)}</span>
                    </p>
                </div>
                <StatusPill status={app.status} />
            </div>

            {/* Handle requested */}
            <div className="px-5 pt-4 pb-2">
                <div className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">Handle solicitado</div>
                <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-p2w-elevated border border-p2w-line">
                    <Crown className="w-3.5 h-3.5 text-p2w-purple" />
                    <span className="font-mono text-white">/c/{app.handle}</span>
                </div>
            </div>

            {/* Metrics grid */}
            <div className="px-5 pt-4 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                <MetricCell icon={Target} label="Entradas" value={m.entries} />
                <MetricCell icon={Trophy} label="Aciertos" value={m.wins} color="green" />
                <MetricCell icon={BarChart3} label="% Acierto" value={`${m.win_rate}%`} color="purple" />
                <MetricCell icon={Award} label="ROI" value={`${m.roi}%`} color={m.roi >= 0 ? "green" : "rose"} />
                <MetricCell icon={Wallet} label="Invertido" value={formatPoints(m.invested)} small />
                <MetricCell icon={TrendingUp} label="Ganado" value={formatPoints(m.won)} small color="green" />
                <MetricCell icon={Users} label="Referidos L1" value={u.referrals_l1} />
                <MetricCell icon={Calendar} label="Días activo" value={u.days_active} />
            </div>

            {/* User stats */}
            <div className="px-5 mt-4 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg bg-p2w-bg/40 border border-p2w-line/50 px-3 py-2">
                    <div className="text-[9px] uppercase text-p2w-gray">Nivel</div>
                    <div className="text-white font-bold number-tabular">{u.level}</div>
                </div>
                <div className="rounded-lg bg-p2w-bg/40 border border-p2w-line/50 px-3 py-2">
                    <div className="text-[9px] uppercase text-p2w-gray">Balance</div>
                    <div className="text-p2w-green font-bold number-tabular">{formatPoints(u.points_balance)}</div>
                </div>
                <div className="rounded-lg bg-p2w-bg/40 border border-p2w-line/50 px-3 py-2">
                    <div className="text-[9px] uppercase text-p2w-gray">Depósitos OK</div>
                    <div className="text-white font-bold number-tabular">{u.deposits_approved}</div>
                </div>
            </div>

            {/* Pitch */}
            {app.pitch && (
                <div className="mx-5 mt-4 rounded-xl bg-p2w-bg/40 border border-p2w-line/50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">Pitch del candidato</div>
                    <p className="mt-1 text-sm text-white/90 leading-relaxed">{app.pitch}</p>
                </div>
            )}

            {/* Footer actions */}
            <div className="px-5 py-4 mt-2 border-t border-p2w-line bg-p2w-bg/30 flex items-center justify-between gap-3 flex-wrap">
                {app.status === "pending" ? (
                    <>
                        <RejectDialog app={app} onDone={onChange} />
                        <ApproveDialog app={app} onDone={onChange} />
                    </>
                ) : (
                    <>
                        <div className="text-xs text-p2w-gray">
                            {app.reviewed_at && <>Revisado: <span className="text-white">{formatDt(app.reviewed_at)}</span></>}
                            {app.review_notes && <div className="mt-1 italic">&ldquo;{app.review_notes}&rdquo;</div>}
                        </div>
                        {app.status === "approved" && u.creator_tier && (
                            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-p2w-green/15 text-p2w-green border border-p2w-green/30 font-semibold uppercase tracking-wider">
                                <Star className="w-3 h-3" /> Tier {u.creator_tier}
                            </span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function MetricCell({ icon: Icon, label, value, color = "default", small }) {
    const colorMap = {
        green: "text-p2w-green",
        purple: "text-p2w-purple",
        rose: "text-rose-400",
        default: "text-white",
    };
    return (
        <div className="rounded-lg bg-p2w-elevated border border-p2w-line/50 px-2.5 py-2">
            <div className="flex items-center gap-1 text-[9px] uppercase text-p2w-gray font-semibold">
                <Icon className="w-2.5 h-2.5" /> {label}
            </div>
            <div className={`mt-0.5 font-sora font-bold ${small ? "text-xs" : "text-base"} ${colorMap[color]} number-tabular truncate`} title={String(value)}>
                {value}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { label: "Pendiente", cls: "bg-amber-400/15 text-amber-400 border-amber-400/30" },
        approved: { label: "Aprobada", cls: "bg-p2w-green/15 text-p2w-green border-p2w-green/30" },
        rejected: { label: "Rechazada", cls: "bg-rose-400/15 text-rose-400 border-rose-400/30" },
        in_review: { label: "En revisión", cls: "bg-blue-400/15 text-blue-400 border-blue-400/30" },
    };
    const i = map[status] || map.pending;
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${i.cls} shrink-0`}>{i.label}</span>;
}

function ApproveDialog({ app, onDone }) {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const close = (v) => { setOpen(v); if (!v) { setStep(1); setNotes(""); } };

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/admin/creator-applications/${app.id}/review`, { decision: "approved", notes });
            toast.success(`@${app.user.username} aprobado como creador con handle /c/${app.handle}`);
            close(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al aprobar");
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogTrigger asChild>
                <Button data-testid={`approve-creator-${app.id}`} size="sm" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold flex-1">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Aprobar
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sora">{step === 1 ? "Aprobar solicitud" : "Confirmar aprobación"}</DialogTitle>
                    <DialogDescription className="text-p2w-gray text-xs">
                        {step === 1 ? "Agrega una nota opcional para el candidato." : "Esta acción activa el perfil de creador y le permite crear mercados."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-xl bg-p2w-elevated border border-p2w-line p-3">
                        <div className="text-[10px] uppercase tracking-wider text-p2w-gray">Candidato</div>
                        <div className="mt-1 text-white font-semibold">@{app.user.username}</div>
                        <div className="text-xs text-p2w-gray mt-1">Handle: <span className="text-p2w-green font-mono">/c/{app.handle}</span></div>
                    </div>
                    {step === 1 ? (
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Nota opcional para el creador…" className="bg-p2w-elevated border-p2w-line text-white" data-testid="approve-notes" />
                    ) : (
                        <div className="rounded-xl bg-p2w-green/5 border border-p2w-green/40 p-4 text-sm">
                            <p className="text-white">Al confirmar:</p>
                            <ul className="mt-2 list-disc list-inside text-p2w-gray space-y-1 text-xs">
                                <li>@{app.user.username} obtendrá el rol <b className="text-p2w-green">creator</b></li>
                                <li>Tier inicial: <b className="text-p2w-green">Bronze</b></li>
                                <li>Podrá crear mercados desde su dashboard</li>
                                <li>Recibirá notificación de aprobación</li>
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    {step === 1 ? (
                        <>
                            <Button variant="outline" onClick={() => close(false)} className="border-p2w-line bg-p2w-elevated text-white">Cancelar</Button>
                            <Button onClick={() => setStep(2)} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold" data-testid="approve-next">Continuar →</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)} className="border-p2w-line bg-p2w-elevated text-white">← Atrás</Button>
                            <Button onClick={submit} disabled={submitting} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold" data-testid="approve-confirm">
                                {submitting ? "Aprobando…" : "Confirmar aprobación"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RejectDialog({ app, onDone }) {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!notes.trim()) { toast.error("Motivo requerido para rechazar"); return; }
        setSubmitting(true);
        try {
            await api.post(`/admin/creator-applications/${app.id}/review`, { decision: "rejected", notes });
            toast.success("Solicitud rechazada");
            setOpen(false); setNotes("");
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid={`reject-creator-${app.id}`} size="sm" variant="outline" className="border-rose-400/40 bg-rose-400/5 text-rose-400 hover:bg-rose-400/10 flex-1">
                    <XCircle className="w-4 h-4 mr-1.5" /> Rechazar
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sora text-rose-400">Rechazar solicitud</DialogTitle>
                    <DialogDescription className="text-p2w-gray text-xs">
                        Explica al candidato por qué su solicitud fue rechazada.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Motivo del rechazo (obligatorio)…"
                        className="bg-p2w-elevated border-p2w-line text-white min-h-[120px]"
                        data-testid="reject-notes"
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="border-p2w-line bg-p2w-elevated text-white">Cancelar</Button>
                    <Button onClick={submit} disabled={submitting || !notes.trim()} className="bg-rose-500 text-white hover:bg-rose-500/90 font-semibold" data-testid="reject-confirm">
                        {submitting ? "Rechazando…" : "Confirmar rechazo"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function formatDt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("es-CO", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16); }
}

/**
 * Manual override: lets an admin directly promote ANY user to Creator without
 * eligibility checks. Useful for grandfathering, sponsored creators, or VIPs.
 */
function ManualGrantDialog({ onDone }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null); // { id, email, username, is_creator, ... }
    const [handle, setHandle] = useState("");
    const [tier, setTier] = useState("bronze");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setQ(""); setResults([]); setSelected(null);
        setHandle(""); setTier("bronze"); setNotes("");
    };
    const close = (v) => { setOpen(v); if (!v) reset(); };

    const runSearch = useCallback(async (term, signal) => {
        setSearching(true);
        try {
            const { data } = await api.get(`/admin/users?q=${encodeURIComponent(term)}`);
            if (!signal.cancelled) setResults((data || []).slice(0, 8));
        } catch (e) {
            if (!signal.cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error en búsqueda");
        } finally {
            if (!signal.cancelled) setSearching(false);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        if (!q || q.trim().length < 2) { setResults([]); return; }
        const term = q.trim();
        const signal = { cancelled: false };
        const t = setTimeout(() => runSearch(term, signal), 250);
        return () => { signal.cancelled = true; clearTimeout(t); };
    }, [q, open, runSearch]);

    const handleNormalized = handle.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");

    const submit = async () => {
        if (!selected) { toast.error("Selecciona un usuario"); return; }
        if (!handleNormalized || handleNormalized.length < 2) { toast.error("Handle inválido (mín 2 caracteres)"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/creators/grant", {
                user_id: selected.id,
                handle: handleNormalized,
                tier,
                notes: notes.trim() || null,
            });
            toast.success(`@${data.username || selected.username} acreditado como creador en /c/${data.handle}`);
            close(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo acreditar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogTrigger asChild>
                <Button
                    data-testid="open-manual-grant-creator"
                    className="bg-p2w-purple text-white hover:bg-p2w-purple/90 font-semibold"
                >
                    <Sparkles className="w-4 h-4 mr-1.5" /> Acreditar manualmente
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-sora flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-p2w-purple" />
                        Acreditación manual de creador
                    </DialogTitle>
                    <DialogDescription className="text-p2w-gray text-xs">
                        Promueve a cualquier usuario al rol Creator sin que cumpla los requisitos.
                        Esta acción queda registrada en auditoría.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Step 1: Search */}
                    {!selected ? (
                        <>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                                    Buscar usuario por email o username
                                </label>
                                <div className="relative mt-1.5">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-p2w-gray" />
                                    <Input
                                        autoFocus
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="ej: juan@correo.com o juanito"
                                        className="bg-p2w-elevated border-p2w-line text-white pl-9"
                                        data-testid="manual-grant-search"
                                    />
                                </div>
                            </div>

                            <div className="max-h-72 overflow-y-auto rounded-xl border border-p2w-line bg-p2w-bg/40 divide-y divide-p2w-line/60">
                                {searching ? (
                                    <div className="p-6 text-center text-xs text-p2w-gray">Buscando…</div>
                                ) : results.length === 0 ? (
                                    <div className="p-6 text-center text-xs text-p2w-gray">
                                        {q.trim().length < 2
                                            ? "Escribe al menos 2 caracteres para buscar"
                                            : "Sin resultados"}
                                    </div>
                                ) : (
                                    results.map((u) => (
                                        <button
                                            key={u.id}
                                            onClick={() => {
                                                if (u.is_creator) {
                                                    toast.error("Este usuario ya es creador");
                                                    return;
                                                }
                                                setSelected(u);
                                                setHandle(u.username || "");
                                            }}
                                            data-testid={`manual-grant-user-${u.username}`}
                                            className={`w-full text-left p-3 hover:bg-p2w-elevated/50 transition flex items-center gap-3 ${
                                                u.is_creator ? "opacity-40 cursor-not-allowed" : ""
                                            }`}
                                            disabled={u.is_creator}
                                        >
                                            <Avatar className="w-9 h-9">
                                                <AvatarImage src={u.avatar_url} />
                                                <AvatarFallback className="bg-p2w-purple text-white text-xs font-bold">
                                                    {(u.username || "?").slice(0, 2).toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white font-semibold truncate">@{u.username}</div>
                                                <div className="text-[11px] text-p2w-gray truncate">{u.email}</div>
                                            </div>
                                            {u.is_creator ? (
                                                <span className="text-[10px] uppercase tracking-wider text-p2w-green font-semibold shrink-0">Ya creator</span>
                                            ) : (
                                                <UserPlus className="w-4 h-4 text-p2w-purple shrink-0" />
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Selected user card */}
                            <div className="rounded-xl bg-p2w-elevated border border-p2w-line p-3 flex items-center gap-3">
                                <Avatar className="w-11 h-11 ring-1 ring-p2w-purple/30">
                                    <AvatarImage src={selected.avatar_url} />
                                    <AvatarFallback className="bg-p2w-purple text-white font-bold">
                                        {(selected.username || "?").slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white font-semibold truncate">@{selected.username}</div>
                                    <div className="text-xs text-p2w-gray truncate">{selected.email}</div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setSelected(null); setHandle(""); }}
                                    className="border-p2w-line bg-transparent text-p2w-gray hover:text-white text-xs"
                                    data-testid="manual-grant-clear-user"
                                >
                                    Cambiar
                                </Button>
                            </div>

                            {/* Handle */}
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                                    Handle público (URL del perfil)
                                </label>
                                <div className="mt-1.5 flex items-center rounded-lg bg-p2w-elevated border border-p2w-line overflow-hidden">
                                    <span className="px-3 text-p2w-gray font-mono text-sm">/c/</span>
                                    <Input
                                        value={handle}
                                        onChange={(e) => setHandle(e.target.value)}
                                        placeholder="handle"
                                        className="bg-transparent border-0 text-white font-mono focus-visible:ring-0"
                                        data-testid="manual-grant-handle"
                                    />
                                </div>
                                {handleNormalized && handleNormalized !== handle && (
                                    <p className="text-[10px] text-p2w-gray mt-1">
                                        Se guardará como: <span className="text-p2w-green font-mono">/c/{handleNormalized}</span>
                                    </p>
                                )}
                            </div>

                            {/* Tier */}
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                                    Tier inicial
                                </label>
                                <div className="mt-1.5 grid grid-cols-3 gap-2">
                                    {["bronze", "silver", "gold"].map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => setTier(t)}
                                            data-testid={`manual-grant-tier-${t}`}
                                            className={`px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-wider transition ${
                                                tier === t
                                                    ? "bg-p2w-purple/20 border-p2w-purple text-p2w-purple"
                                                    : "bg-p2w-elevated border-p2w-line text-p2w-gray hover:text-white"
                                            }`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                                    Nota interna (opcional)
                                </label>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Razón de la acreditación manual (queda en auditoría)…"
                                    className="bg-p2w-elevated border-p2w-line text-white mt-1.5"
                                    data-testid="manual-grant-notes"
                                />
                            </div>

                            <div className="rounded-lg bg-p2w-purple/5 border border-p2w-purple/30 p-3 text-xs text-p2w-gray">
                                <p className="text-white font-semibold">Al confirmar:</p>
                                <ul className="mt-1 list-disc list-inside space-y-0.5">
                                    <li>@{selected.username} será promovido a <b className="text-p2w-purple">Creator</b> con tier <b className="text-p2w-purple">{tier}</b></li>
                                    <li>Se asignará el handle <span className="font-mono text-p2w-green">/c/{handleNormalized}</span></li>
                                    <li>Se registra una solicitud sintética con flag <code className="text-p2w-purple">manual_grant</code> para auditoría</li>
                                </ul>
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => close(false)} className="border-p2w-line bg-p2w-elevated text-white">
                        Cancelar
                    </Button>
                    {selected && (
                        <Button
                            onClick={submit}
                            disabled={submitting || !handleNormalized || handleNormalized.length < 2}
                            className="bg-p2w-purple text-white hover:bg-p2w-purple/90 font-semibold"
                            data-testid="manual-grant-confirm"
                        >
                            {submitting ? "Acreditando…" : "Acreditar como creador"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
